import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Link as MuiLink,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../hooks'
import { login } from '../Store/AuthSlice'
import { Visibility, VisibilityOff } from '@mui/icons-material'

function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { register, handleSubmit, watch } = useForm()
  const email = watch('email')
  const password = watch('password')
  const dispatch = useAppDispatch()
  const loading = useAppSelector((state) => state.auth.loading)
  const errorMessage = useAppSelector((state) => state.auth.errorMessage)
  const [showPassword, setShowPassword] = useState(false)

  const handleLogin = async () => {
    const loginResult = await dispatch(login({ email, password }))
    if (login.fulfilled.match(loginResult)) {
      navigate('/')
    }
  }

  const handleRegister = () => {
    navigate('/register')
  }

  const handleForgotPassword = () => {
    navigate('/forgot-password')
  }

  return (
    <Container maxWidth="xs">
      <Box
        component="form"
        sx={{
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleLogin)}
      >
        <h2>{t('loginBtn')}</h2>
        <TextField
          {...register('email', {
            required: t('requiredField'),
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: t('invalidEmail'),
            },
          })}
          required
          label={t('emailLabel')}
          variant="outlined"
          fullWidth
          margin="normal"
        />
        <TextField
          {...register('password', { required: t('requiredField') })}
          required
          label={t('passwordLabel')}
          variant="outlined"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          margin="normal"
          InputProps={{
            endAdornment: (
              <span style={{ cursor: 'pointer', height: '25px' }} onClick={() => setShowPassword((v) => !v)}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </span>
            ),
          }}
        />
        <Grid container spacing={2} justifyContent="center" marginTop={2}>
          <Grid item>
            <Button type="submit" variant="contained" color="primary" onClick={handleLogin} data-testid="login">
              {t('loginBtn')}
              {loading && <CircularProgress size={24} />}
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" onClick={handleRegister} data-testid="register">
              {t('registerBtn')}
            </Button>
          </Grid>
        </Grid>
        {errorMessage && (
          <Grid container spacing={2} justifyContent="center" marginTop={2}>
            <Box sx={{ color: 'error.main' }}>{errorMessage}</Box>
          </Grid>
        )}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <MuiLink
            component="a"
            variant="body2"
            onClick={handleForgotPassword}
            sx={{ cursor: 'pointer' }}
          >
            {t('forgotPasswordLink')}
          </MuiLink>
        </Box>
      </Box>
    </Container>
  )
}

export default LoginPage
